/**
 * La prenotazione, dalla parte del sito.
 *
 * Due chiamate e basta, tutte e due sulla rotta APERTA del ticket-service:
 * prenotare e ritrovare. Aperta perche' la prenotazione e' fatta per chi non
 * ha un conto -- e per questo da qui non parte e non torna niente di
 * personale. La skin la dice l'intestazione TENANT_ID, come per la schedina
 * pubblica.
 *
 * COSA NON FA: non gioca. Una prenotazione riaperta diventa una schedina
 * compilata, e per giocarla si preme Gioca come per qualunque altra.
 */
import {intestazioni, errore} from '../components/Schedina/inviaGiocata';
import i18next from 'i18next';

function indirizzoGateway() {
  const env = (typeof window !== 'undefined' && window.env) ? window.env : {};
  return env.REACT_APP_API_URL || 'http://localhost:9001';
}

const BASE = () => `${indirizzoGateway()}/v1/ticket-service/public/prenotazioni`;

const RETE = {
  code: 'RETE',
  status: 0,
  get message() { return i18next.t('Non si riesce a raggiungere il server: riprova fra poco'); },
};

/**
 * Prenota.
 * @param richiesta la stessa forma di una giocata (componiRichiesta)
 * @returns {Promise<{ok: boolean, prenotazione?: Object, errore?: Object}>}
 */
export async function prenotaGiocata(richiesta) {
  try {
    const risposta = await fetch(BASE(), {
      method: 'POST',
      headers: intestazioni(),
      body: JSON.stringify(richiesta),
    });
    const corpo = await risposta.json().catch(() => null);
    if (risposta.ok) return {ok: true, prenotazione: corpo};
    return {ok: false, errore: errore(risposta.status, corpo)};
  } catch (e) {
    return {ok: false, errore: RETE};
  }
}

/**
 * Ritrova dal codice, con i prezzi di adesso riga per riga (`adesso`).
 * Scaduta torna lo stesso, con `scaduta: true`: e' la schedina a dirlo.
 */
export async function leggiPrenotazione(codice) {
  const pulito = String(codice || '').trim().toUpperCase();
  if (!pulito) {
    return {ok: false, errore: {code: 'CODICE_VUOTO', status: 0, message: i18next.t('Scrivi il codice')}};
  }
  try {
    const risposta = await fetch(`${BASE()}/${encodeURIComponent(pulito)}`, {
      method: 'GET',
      headers: intestazioni(),
    });
    const corpo = await risposta.json().catch(() => null);
    if (risposta.ok) return {ok: true, prenotazione: corpo};
    return {ok: false, errore: errore(risposta.status, corpo)};
  } catch (e) {
    return {ok: false, errore: RETE};
  }
}

/** Il collegamento che riapre la schedina: il dominio e' quello della skin
 *  dove la prenotazione e' nata, cioe' questo. */
export function linkDi(codice) {
  const origine = (typeof window !== 'undefined' && window.location)
    ? window.location.origin : '';
  return `${origine}/p/${encodeURIComponent(codice)}`;
}

/**
 * Dalla prenotazione alle selezioni della schedina (allBets), con i PREZZI
 * DI ADESSO: quelli di quando e' stata fatta sono scritti accanto, ma vale il
 * prezzo del momento in cui si gioca, e lo dice la maschera stessa.
 *
 * Le selezioni che non si giocano piu' (partita cominciata, mercato chiuso)
 * restano fuori, e tornano in `scartate` per dirlo per nome: una schedina
 * che le tenesse dentro verrebbe rifiutata al Gioca senza spiegare quale.
 *
 * @returns {{selezioni: Array, scartate: Array, cambiate: Array, importo: number|null,
 *            richiesta: Object}}
 */
export function schedinaDa(prenotazione) {
  const richiesta = (prenotazione && prenotazione.richiesta) || {};
  const adesso = {};
  (prenotazione && prenotazione.adesso || []).forEach((a) => {
    adesso[`${a.eventId}|${a.marketKey}|${a.selectionCode}`] = a;
  });
  const nomi = {};
  (prenotazione && prenotazione.selezioni || []).forEach((s) => {
    nomi[`${s.eventId}|${s.marketKey}|${s.selectionCode}`] = s;
  });

  const selezioni = [];
  const scartate = [];
  const cambiate = [];

  (richiesta.bets || []).forEach((evento) => {
    const markets = [];
    (evento.markets || []).forEach((mercato) => {
      const odds = [];
      (mercato.odds || []).forEach((quota) => {
        const k = `${evento.eventId}|${mercato.id}|${quota.id}`;
        const a = adesso[k];
        const nome = nomi[k] || {};
        const etichetta = `${nome.eventName || evento.matchName || evento.eventId}`
            + ` · ${quota.segno || quota.oddName || quota.id}`;
        /* Senza `adesso` (prenotazione appena fatta) vale il prezzo scritto;
           con `adesso` vale il catalogo, e se non si gioca piu' si scarta. */
        if (a && !a.giocabile) {
          scartate.push({etichetta, motivo: a.motivo || 'NON_GIOCABILE'});
          return;
        }
        const prezzo = a && a.prezzoAdesso != null ? Number(a.prezzoAdesso) : Number(quota.value);
        if (a && a.cambiata) {
          cambiate.push({etichetta, da: Number(a.prezzoAllora), a: prezzo});
        }
        odds.push({
          id: quota.id,
          sbv: quota.sbv,
          oddName: quota.oddName,
          segno: quota.segno || quota.oddName,
          value: prezzo,
        });
      });
      if (odds.length > 0) {
        markets.push({id: mercato.id, marketName: mercato.marketName, odds});
      }
    });
    if (markets.length > 0) {
      const prima = (evento.markets && evento.markets[0] && evento.markets[0].odds
          && evento.markets[0].odds[0]) || {};
      const nome = nomi[`${evento.eventId}|${evento.markets[0].id}|${prima.id}`] || {};
      selezioni.push({
        eventId: evento.eventId,
        eventNumber: evento.eventNumber || nome.eventNumber || null,
        matchName: nome.eventName || evento.matchName,
        competitionName: evento.competitionName || nome.competitionName || '',
        matchDate: nome.inizio || null,
        markets,
      });
    }
  });

  /* L'importo: per singola e multipla e' `amount`; per l'integrale e il
     sistema la schedina ha i suoi campi e qui si ridà il totale, che e'
     l'unica cifra che la maschera scrive. */
  const importo = richiesta.amount != null ? Number(richiesta.amount) : null;

  return {selezioni, scartate, cambiate, importo, richiesta};
}
